import React, { useState } from 'react';
import { pb } from '@/lib/pocketbase';
import { useSearchParams } from 'react-router-dom';
// import Lottie from 'lottie-react';
// import confettiAnimation from 'public/lottie/lottie.json';

// Define types for survey questions and answers
type Question = {
  id: string;
  text: string;
  text_np?: string;
  options?: string[];
  options_np?: string[];
  type: 'multiple-choice' | 'text' | 'phone';
};

type SurveyConfig = {
  title?: string;
  title_np?: string;
  thankYouTitle?: string;
  thankYouTitle_np?: string;
  thankYouText?: string;
  thankYouText_np?: string;
  questions?: Question[];
};

type SurveyAnswers = {
  [key: string]: string | string[];
};

interface SurveySectionProps {
  survey?: SurveyConfig;
}

export const SurveySection: React.FC<SurveySectionProps> = ({ survey }) => {
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale') || 'en';
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const [showConfetti, setShowConfetti] = useState(false);

  const fallbackSurveyTitle = locale === 'np' ? 'हामीलाई तपाईंको विचार बताउनुहोस्' : 'Share Your Thoughts With Us';

  // Define survey questions
  const defaultQuestions: Question[] = [
      {
        id: 'discover_method',
        text: locale === 'np' ? 'तपाईंले पहिलो पटक Dailo Krishi को Malda आँपको बारेमा कहाँबाट थाहा पाउनुभयो?' : 'How did you first learn about Dailo Krishi’s Malda Mango?',
        text_np: 'तपाईंले पहिलो पटक Dailo Krishi को Malda आँपको बारेमा कहाँबाट थाहा पाउनुभयो?',
        type: 'multiple-choice',
        options: locale === 'np'
          ? ['सोसल मिडिया', 'साथी/परिवार', 'लोकल पसल', 'अनलाइन विज्ञापन', 'अन्य']
          : ['Social Media', 'Friends/Family', 'Local Vendor', 'Online Ad', 'Other'],
        options_np: ['सोसल मिडिया', 'साथी/परिवार', 'लोकल पसल', 'अनलाइन विज्ञापन', 'अन्य']
      },
      {
        id: 'purchase_intent',
        text: locale === 'np' ? 'तपाईंले Malda आँप किन किन्नु भयो?' : 'What made you want to buy the Malda mango?',
        text_np: 'तपाईंले Malda आँप किन किन्नु भयो?',
        type: 'multiple-choice',
        options: locale === 'np'
          ? ['धेरै चर्चामा थियो', 'Dailo मा विश्वास लाग्यो', 'लोकल/जैविक मन पर्छ', 'मूल्य ठीक लाग्यो']
          : ['Heard it\'s legendary', 'Trusted source (Dailo)', 'Wanted local/organic', 'Price looked fair'],
        options_np: ['धेरै चर्चामा थियो', 'Dailo मा विश्वास लाग्यो', 'लोकल/जैविक मन पर्छ', 'मूल्य ठीक लाग्यो']
      },
      {
        id: 'mango_use',
        text: locale === 'np' ? 'तपाईं प्रायः आँपलाई कसरी खाने गर्नुहुन्छ?' : 'How do you mostly consume mangoes?',
        text_np: 'तपाईं प्रायः आँपलाई कसरी खाने गर्नुहुन्छ?',
        type: 'multiple-choice',
        options: locale === 'np'
          ? ['सिधै खाने', 'जुस/स्मूदी', 'अचार/ज्याम', 'मिठाई बनाउन', 'अन्य']
          : ['As is (raw)', 'Smoothies/Juice', 'Pickles/Jam', 'Desserts', 'Other'],
        options_np: ['सिधै खाने', 'जुस/स्मूदी', 'अचार/ज्याम', 'मिठाई बनाउन', 'अन्य']
      },
      {
        id: 'ripeness_preference',
        text: locale === 'np' ? 'तपाईंलाई कस्तो आँप मन पर्छ?' : 'How do you prefer your mango when it arrives?',
        text_np: 'तपाईंलाई कस्तो आँप मन पर्छ?',
        type: 'multiple-choice',
        options: locale === 'np'
          ? ['पुर्ण पाकेको', 'अलिकति काँचो (घरमै पाकाउने)', 'जसरी भए पनि ठीकै']
          : ['Completely ripe', 'Slightly raw (ripen at home)', 'Doesn’t matter'],
        options_np: ['पुर्ण पाकेको', 'अलिकति काँचो (घरमै पाकाउने)', 'जसरी भए पनि ठीकै']
      },
      {
        id: 'purchase_frequency',
        text: locale === 'np' ? 'मौसमभरि तपाईं कति पटक आँप किन्नुहुन्छ?' : 'How often do you buy mangoes during the season?',
        text_np: 'मौसमभरि तपाईं कति पटक आँप किन्नुहुन्छ?',
        type: 'multiple-choice',
        options: locale === 'np'
          ? ['हरेक हप्ता', '२ हप्तामा एकपटक', 'महिनामा एकपटक', 'एक/दुई पटक मात्रै']
          : ['Weekly', 'Every 2 weeks', 'Once a month', 'Only once/twice'],
        options_np: ['हरेक हप्ता', '२ हप्तामा एकपटक', 'महिनामा एकपटक', 'एक/दुई पटक मात्रै']
      },
      {
        id: 'satisfaction',
        text: locale === 'np' ? 'Malda आँप किन्दा तपाईं कत्तिको सन्तुष्ट हुनुहुन्छ?' : 'How satisfied are you with your Malda mango experience?',
        text_np: 'Malda आँप किन्दा तपाईं कत्तिको सन्तुष्ट हुनुहुन्छ?',
        type: 'multiple-choice',
        options: locale === 'np'
          ? ['धेरै सन्तुष्ट', 'सन्तुष्ट', 'ठीकै', 'असन्तुष्ट']
          : ['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied'],
        options_np: ['धेरै सन्तुष्ट', 'सन्तुष्ट', 'ठीकै', 'असन्तुष्ट']
      },
      {
        id: 'improvement',
        text: locale === 'np' ? 'हामीले के सुधार गर्न सक्छौं?' : 'What could we do better?',
        text_np: 'हामीले के सुधार गर्न सक्छौं?',
        type: 'multiple-choice',
        options: locale === 'np'
          ? ['प्याकेजिङ', 'डेलिभरी समय', 'पक्ने अवस्था', 'मूल्य', 'सबै ठिक छ']
          : ['Packaging', 'Delivery Time', 'Ripeness', 'Price', 'Nothing – All Good'],
        options_np: ['प्याकेजिङ', 'डेलिभरी समय', 'पक्ने अवस्था', 'मूल्य', 'सबै ठिक छ']
      },    
    {
      id: 'phone_number',
      text: locale === 'np' ? 'हामीलाई तपाईंको फोन नम्बर दिनुहोस्' : 'Please provide your phone number',
      text_np: 'हामीलाई तपाईंको फोन नम्बर दिनुहोस्',
      type: 'phone'
    }
  ];

  const questions = survey?.questions?.length ? survey.questions : defaultQuestions;
  const currentQuestion = questions[currentQuestionIndex];
  const getLocalizedText = (en?: string, np?: string) => (locale === 'np' ? (np || en || '') : (en || np || ''));
  const title = getLocalizedText(survey?.title, survey?.title_np) || fallbackSurveyTitle;
  const thankYouTitle = getLocalizedText(survey?.thankYouTitle, survey?.thankYouTitle_np) || (locale === 'np' ? 'धन्यवाद!' : 'Thank you!');
  const thankYouText = getLocalizedText(survey?.thankYouText, survey?.thankYouText_np) || (
    locale === 'np'
      ? 'तपाईंको प्रतिक्रियाको लागि धन्यवाद। हामी तपाईंलाई छिट्टै सम्पर्क गर्नेछौं।'
      : 'Thank you for your feedback. We will be in touch with you soon.'
  );

  const handleOptionSelect = (option: string) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: option
    });

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: e.target.value
    });
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numeric input for phone numbers
    const value = e.target.value.replace(/\D/g, '');
    setAnswers({
      ...answers,
      [currentQuestion.id]: value
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Format the survey data for submission
      const surveyData = {
        survey: JSON.stringify(answers)
      };

      // Submit the survey data to the API
      await pb.collection('survey').create(surveyData);

      // Show confetti and mark as submitted
      // setShowConfetti(true);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit survey:', err);
      setError(locale === 'np' 
        ? 'सर्वेक्षण पेश गर्दा त्रुटि भयो। कृपया फेरि प्रयास गर्नुहोस्।' 
        : 'Failed to submit survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  if (submitted) {
    return (
      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">
            {thankYouTitle}
          </h2>
          <p className="text-lg text-gray-600">
            {thankYouText}
          </p>
        </div>
      </section>
    );
  }

  return (
      <section className="bg-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center text-gray-800">
          {title}
        </h2>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <p className="text-sm text-gray-500 mb-1">
              {locale === 'np' 
                ? `प्रश्न ${currentQuestionIndex + 1}/${questions.length}` 
                : `Question ${currentQuestionIndex + 1}/${questions.length}`}
            </p>
            <h3 className="text-xl font-semibold mb-4">{currentQuestion.text}</h3>

            {currentQuestion.type === 'multiple-choice' && currentQuestion.options && (
              <div className="space-y-2">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleOptionSelect(option)}
                    className={`w-full py-3 px-4 text-left rounded-md border transition-colors ${
                      answers[currentQuestion.id] === option 
                        ? 'bg-[rgba(0,63,40,0.1)] border-[rgba(0,63,40,1)] text-[rgba(0,63,40,1)]' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            {currentQuestion.type === 'text' && (
              <input
                type="text"
                value={(answers[currentQuestion.id] as string) || ''}
                onChange={handleTextInput}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-2 focus:ring-[rgba(0,63,40,0.3)] focus:border-[rgba(0,63,40,1)] outline-none"
                placeholder={locale === 'np' ? 'तपाईंको जवाफ यहाँ टाइप गर्नुहोस्...' : 'Type your answer here...'}
              />
            )}

            {currentQuestion.type === 'phone' && (
              <input
                type="tel"
                value={(answers[currentQuestion.id] as string) || ''}
                onChange={handlePhoneInput}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-2 focus:ring-[rgba(0,63,40,0.3)] focus:border-[rgba(0,63,40,1)] outline-none"
                placeholder={locale === 'np' ? 'तपाईंको फोन नम्बर' : 'Your phone number'}
              />
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentQuestionIndex === 0}
              className={`px-4 py-2 rounded-md ${
                currentQuestionIndex === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {locale === 'np' ? 'पछाडि' : 'Back'}
            </button>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!answers[currentQuestion.id]}
                className={`px-4 py-2 rounded-md ${
                  !answers[currentQuestion.id] 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-[rgba(0,63,40,1)] text-white hover:bg-[rgba(0,63,40,0.8)]'
                }`}
              >
                {locale === 'np' ? 'अर्को' : 'Next'}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!answers[currentQuestion.id] || isSubmitting}
                className={`px-4 py-2 rounded-md ${
                  !answers[currentQuestion.id] || isSubmitting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-[rgba(0,63,40,1)] text-white hover:bg-[rgba(0,63,40,0.8)]'
                }`}
              >
                {isSubmitting 
                  ? (locale === 'np' ? 'पेश गर्दै...' : 'Submitting...') 
                  : (locale === 'np' ? 'पेश गर्नुहोस्' : 'Submit')}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
